// One ride, editable: the time as the hero, the band to drag it along, what the new time means
// (dark minutes, feels-like, kit, home by), and the routes that fit. A proposed ride changes at
// once with an Undo; a booked ride asks first, because the diary moves with it.
import { useEffect, useMemo, useState } from "preact/hooks";
import { useMusette, moveRide, setRoute } from "../lib/store";
import { sunTimes, hhmm, toMin } from "../lib/sun";
import { hourlyDay, kitFor, type HourlyDay } from "../lib/weather";
import { darkMinutes, AXIS_START, type Day, type Kept } from "../lib/band";
import { wallsFor, routeOptions } from "../lib/day";
import { unlitAfterSunrise, fitsLight } from "../lib/ride";
import { dayLong } from "../lib/dates";
import { BandScrub } from "./BandScrub";
import type { PlanDay } from "../lib/types";
import { homeOf } from "../data/empty";

type Props = { day: PlanDay; label?: string; toast: (text: string, undo?: () => void) => void; kept?: Kept[]; compact?: boolean };

export function RideEditor({ day, label, toast, kept = [], compact }: Props) {
  const s = useMusette();
  const base = { start: toMin(day.start!), end: toMin(day.end!), route: day.route ?? "city-loop" };
  const [live, setLive] = useState(base);
  const [pending, setPending] = useState<typeof base | null>(null);
  const [busy, setBusy] = useState(false);
  const [hourly, setHourly] = useState<HourlyDay | null>(null);
  useEffect(() => { setLive(base); setPending(null); }, [day.date, day.start, day.end, day.route]);
  useEffect(() => { hourlyDay(homeOf(s).lat, homeOf(s).lon, day.date).then(setHourly); }, [day.date]);

  const home = homeOf(s);
  const sun = sunTimes(home.lat, home.lon, day.date);
  const walls = useMemo(() => wallsFor(s, day.date, kept.map((k) => ({ ...k }))), [day.date, s.settings]);
  const dark = Math.round(darkMinutes(live, sun.rise, sun.set));
  const hourIx = Math.floor((live.start - AXIS_START) / 60);
  const feels = hourly && hourIx >= 0 && hourIx < hourly.feels.length ? hourly.feels[hourIx] : null;
  const kit = feels != null ? kitFor(feels, s.settings.kit) : day.kit ?? null;
  const route = s.routes.find((r) => r.id === live.route);
  const routeName = route?.n ?? "City loop, lit";
  const lightOk = !route ? true : live.start < 12 * 60 ? unlitAfterSunrise(route, day.date, hhmm(live.start), home.lat, home.lon) : fitsLight(route, day.date, hhmm(live.start), home.lat, home.lon, s.settings.latestFinish, s.settings.bufferMin, s.settings.sunsetRule !== false);
  const options = routeOptions(s, day.date, live.start, live.end, walls).filter((o) => o.fits || o.id === live.route).slice(0, 6);
  const booked = day.status === "booked";
  const done = day.status === "done" || s.rides.some((r) => r.d === day.date && r.km >= 15);
  const changed = live.start !== base.start || live.route !== base.route;

  const commit = async (next: typeof base) => {
    if (next.start === base.start && next.end === base.end && next.route === base.route) { setPending(null); return; }
    if (booked) { setPending(next); return; }
    const prev = base;
    if (next.route !== base.route) await setRoute(day.date, next.route, hhmm(next.start), hhmm(next.end));
    else await moveRide(day.date, { start: hhmm(next.start), end: hhmm(next.end) });
    toast(next.route !== prev.route ? `${s.routes.find((r) => r.id === next.route)?.n ?? "City loop, lit"}, ${hhmm(next.start)} to ${hhmm(next.end)}` : `Moved to ${hhmm(next.start)}`, () => {
      if (next.route !== prev.route) setRoute(day.date, prev.route, hhmm(prev.start), hhmm(prev.end));
      else moveRide(day.date, { start: hhmm(prev.start), end: hhmm(prev.end) });
    });
  };
  const confirm = async () => {
    if (!pending) return;
    setBusy(true);
    const msg = await moveRide(day.date, { start: hhmm(pending.start), end: hhmm(pending.end), route: pending.route !== base.route ? pending.route : undefined });
    setBusy(false); setPending(null); toast(msg);
  };

  const scrubDay: Day = { date: day.date, rise: sun.rise, set: sun.set, hourly, rides: [{ start: live.start, end: live.end, done }], kept };

  return (
    <div class={`editor${compact ? " compact" : ""}`}>
      {label && <span class="label amber">{label}</span>}
      <div class="editor-time">
        <span class="hero-n">{hhmm(live.start)}</span>
        <span class="editor-to"><span class="label">to</span><span class="stat-n">{hhmm(live.end)}</span></span>
      </div>
      <span class="lede-s">{routeName}{route ? ` · ${route.km} km, ${route.gain} m` : ""}</span>

      <BandScrub day={scrubDay} walls={walls} editable={!done} fold={s.settings.band !== "full"} label={`${dayLong(day.date)}'s ride`}
        onMove={(a, b) => setLive({ ...live, start: a, end: b })}
        onCommit={(a, b) => commit({ ...live, start: a, end: b })} />

      <div class="readouts" aria-live="polite">
        <div><span class="label">Dark</span><span class="stat-n">{dark ? `${dark}` : "0"}<small> min</small></span></div>
        <div><span class="label">Feels</span><span class="stat-n">{feels != null ? `${feels}°` : "–"}</span></div>
        <div><span class="label">Home</span><span class="stat-n">{hhmm(live.end)}</span></div>
      </div>
      <span class="soft editor-line">
        {!lightOk ? `${routeName}'s lanes would be dark at ${hhmm(live.start)}. The city loop fits.`
          : kit ? `${kit[0].toUpperCase() + kit.slice(1)}${feels != null ? ", from the forecast" : ""}.${dark ? ` Lights on ${live.start < sun.rise ? `until ${hhmm(sun.rise)}` : `from ${hhmm(sun.set)}`}.` : ""}`
          : "Kit from the forecast, within 16 days."}
      </span>

      {!done && options.length > 1 && (
        <div class="chips" role="radiogroup" aria-label="Route">
          {options.map((o) => (
            <button type="button" role="radio" aria-checked={o.id === live.route} class={`chip${o.id === live.route ? " on" : ""}`}
              onClick={() => { const next = { start: o.start, end: o.end, route: o.id }; setLive(next); commit(next); }}>
              <span>{o.name}</span>
              <small>{o.km ? `${o.km} km · ${hhmm(o.start)}` : `${o.end - o.start} min`}</small>
            </button>
          ))}
        </div>
      )}

      {!done && <span class="label hint">{booked ? "Drag the ride to move it. The diary follows when you say." : "Drag the ride to move it."}</span>}

      {pending && changed && (
        <div class="confirm">
          <button type="button" class="pill" disabled={busy} onClick={confirm}>{busy ? "Moving" : `Move it in the diary to ${hhmm(pending.start)}`}</button>
          <button type="button" class="text-btn" onClick={() => { setLive(base); setPending(null); }}>Undo</button>
        </div>
      )}
    </div>
  );
}
